import { ColumnFiltersState, Row, SortingState } from "@tanstack/react-table";

import { SearchFilterState } from "@/components/spatialAnalysis/spatialAnalysisTable";
import { normalizeFilterValue } from "@/lib/utils";
import { DataSchemaType, DatasetDataType } from "@/types/dataset";

import { formatNumberRaw } from "./number";

export type DataRowValue = string | number | boolean | null | undefined;

export type DetailRow = Record<string, DataRowValue>;
interface Schema {
  attribute_name: string;
  attribute_column?: string;
  data_type: DataSchemaType;
}

// Pre-computed schema metadata to avoid redundant operations in the row loop
interface ProcessedSchema {
  key: string;
  isNumeric: boolean;
  formatType: "decimal" | "integer";
}

export const formatDatasetRows = (
  data: DetailRow[],
  schema?: Schema[]
): DetailRow[] => {
  if (!schema) return data;
  // Phase 1 optimization: Pre-compute all schema metadata once
  // This avoids calling normalizeFilterValue() and computing flags for each row
  const processedSchema: ProcessedSchema[] = schema.map(
    ({ attribute_column, attribute_name, data_type }) => {
      const isNumeric =
        data_type === DataSchemaType.FLOAT || data_type === DataSchemaType.INT;

      return {
        key: normalizeFilterValue(attribute_column || attribute_name),
        isNumeric,
        formatType: data_type === DataSchemaType.FLOAT ? "decimal" : "integer"
      };
    }
  );

  const result = data.map((row) => {
    const formattedRow = { ...row };

    for (const { key, isNumeric, formatType } of processedSchema) {
      const rawValue = row[key];

      if (rawValue === null || rawValue === undefined) continue;

      if (isNumeric) {
        // Use formatNumberRaw which uses cached Intl.NumberFormat instances
        formattedRow[key] = formatNumberRaw(Number(rawValue), formatType);
      } else {
        formattedRow[key] = String(rawValue);
      }
    }

    return formattedRow;
  });

  return result;
};

export const includesStringFilter = <T>(
  row: Row<T>,
  columnId: string,
  filterValue: string
): boolean => {
  const value = row.getValue(columnId);

  return String(value)
    .toLowerCase()
    .includes(String(filterValue).toLowerCase());
};

export const getTableState = (
  currentPage: number,
  pageSize: number,
  sorting: SortingState,
  columnFilters: ColumnFiltersState
) => ({
  pagination: {
    pageIndex: currentPage - 1,
    pageSize
  },
  sorting,
  columnFilters
});

export const generateColumnFilters = (
  filterState: SearchFilterState
): ColumnFiltersState => {
  const { query, query_type, query_column } = filterState;

  if (!query || !query_column || !query_type) return [];

  return query
    .map((value, i) => {
      const column = query_column[i];
      const type = query_type[i];

      if (!value || !column || !type) return null;

      const cleanValue =
        type === DatasetDataType.TYPE_INTEGER ||
        type === DatasetDataType.TYPE_FLOAT
          ? String(value).replaceAll(",", "")
          : value;

      return { id: column, value: cleanValue };
    })
    .filter((f): f is { id: string; value: string } => f !== null);
};
